/**
 * Collection Scope Service
 *
 * Persists the biblio collection a user has selected to scope a
 * deep-literature-review chat session, so the selection survives a page reload
 * and is restored when the user switches back to a previous chat.
 *
 * Each session has at most one saved collection, stored server-side as a single
 * row in the chat-session-collection-scope DynamoDB table keyed by session ID.
 * This is kept separate from the generic chat-sessions store, which must stay
 * agent-agnostic.
 */

// Cached API base URL loaded from aws-exports.json.
let COLLECTION_SCOPE_API_URL = ""

/**
 * Load the collection-scope API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/collection-scope path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the collection-scope API endpoint.
 * @throws Error if the aws-exports.json config cannot be loaded.
 */
async function loadApiUrl(): Promise<string> {
  if (COLLECTION_SCOPE_API_URL) {
    return COLLECTION_SCOPE_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    COLLECTION_SCOPE_API_URL = baseUrl
      ? `${baseUrl}/api/collection-scope`
      : "/api/collection-scope"
    return COLLECTION_SCOPE_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Collection scope API URL not configured")
  }
}

/**
 * The saved collection scope for a session, as returned by the API.
 */
export interface CollectionScope {
  /**
   * The saved collection id, or null when no collection has been saved for the
   * session (i.e. the session is scoped to the full corpus).
   */
  collectionId: number | null
}

/**
 * Read the saved collection selection for a session.
 *
 * Sends a GET request to /api/collection-scope/{sessionId}. Used when a session
 * is opened (switched to from the sidebar, or restored after a reload) to
 * restore the collection the user had selected for that chat.
 *
 * @param sessionId - The chat session UUID whose saved scope to read.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The saved collection scope (collectionId is null when none saved).
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getCollectionScope(
  sessionId: string,
  accessToken: string
): Promise<CollectionScope> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to get collection scope: HTTP ${response.status} — ${errorBody}`
    )
  }

  const data: CollectionScope = await response.json()
  return data
}

/**
 * Save (create or overwrite) the selected collection for a session.
 *
 * Sends a PUT request to /api/collection-scope/{sessionId} with the selected
 * collection id. Overwrites any prior selection (single-select, last-write-wins).
 *
 * @param sessionId - The chat session UUID to save the scope for.
 * @param collectionId - The selected biblio collection id, or null for "None"
 *   (the full corpus). Null is persisted as a first-class value, not a clear,
 *   so it restores on reload like any other selection.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function saveCollectionScope(
  sessionId: string,
  collectionId: number | null,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ collectionId }),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to save collection scope: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * Clear the saved collection selection for a session.
 *
 * Sends a DELETE request to /api/collection-scope/{sessionId}, removing the
 * whole row. Idempotent — deleting a session with no saved scope succeeds as a
 * no-op. Used to fully remove the scope when a session is deleted, so the row
 * does not linger.
 *
 * @param sessionId - The chat session UUID whose saved scope row to remove.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function clearCollectionScope(
  sessionId: string,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to clear collection scope: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}
